import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { Menu, X, Music, Shield, Sun, Moon, LogIn } from 'lucide-react';
import { useState } from 'react';

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="flex items-center gap-1.5 text-gray-400 hover:text-gray-50 transition px-2 py-1.5 rounded-lg hover:bg-gray-800"
      title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
    >
      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
    </button>
  );
}

export default function Header() {
  const { user, profile, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className="bg-gray-900 border-b border-gray-800"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 text-gray-50 font-bold text-xl">
            <Music className="w-6 h-6 text-indigo-400" />
            Nokset
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/search" className="text-gray-300 hover:text-gray-50 transition">
              Find Bands
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-gray-300 hover:text-gray-50 transition">
                  Dashboard
                </Link>
                {profile?.is_admin && (
                  <Link to="/admin" className="flex items-center gap-1 text-red-400 hover:text-red-300 transition">
                    <Shield className="w-4 h-4" />
                    Admin
                  </Link>
                )}
                <ThemeToggle />
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400">
                    {profile?.display_name ?? user.email}
                  </span>
                  <button
                    onClick={signOut}
                    className="text-sm text-gray-400 hover:text-gray-50 transition"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            ) : (
              <>
                <ThemeToggle />
                <Link to="/login" className="text-gray-300 hover:text-gray-50 transition">
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                >
                  Sign Up
                </Link>
              </>
            )}
          </nav>

          {/* Mobile toggle */}
          <div className="flex md:hidden items-center gap-2">
            {!user && (
              <Link
                to="/login"
                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition"
              >
                <LogIn className="w-4 h-4" />
                Sign In
              </Link>
            )}
            <ThemeToggle />
            <button
              className="text-gray-300"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {menuOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <Link
              to="/search"
              className="block text-gray-300 hover:text-gray-50 py-2"
              onClick={() => setMenuOpen(false)}
            >
              Find Bands
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block text-gray-300 hover:text-gray-50 py-2"
                  onClick={() => setMenuOpen(false)}
                >
                  Dashboard
                </Link>
                {profile?.is_admin && (
                  <Link
                    to="/admin"
                    className="block text-red-400 hover:text-red-300 py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                )}
                <button
                  onClick={() => { signOut(); setMenuOpen(false); }}
                  className="block text-gray-400 hover:text-gray-50 py-2"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-gray-300 hover:text-gray-50 py-2"
                  onClick={() => setMenuOpen(false)}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="block bg-indigo-600 hover:bg-indigo-700 text-white text-center py-2.5 rounded-lg font-medium transition"
                  onClick={() => setMenuOpen(false)}
                >
                  Create an Account
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
